import { Button } from '@mui/material';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { MAIN_CONTENT_ID } from '../../constants/Accessibility';

export default function SkipToMainContent({
  targetId = MAIN_CONTENT_ID,
  triggerSelector = '.teacher-top-app-bar',
  onSkip,
}) {
  // const skipButtonRef = useRef < HTMLButtonElement > (null);
  const [hasShownSkip, setHasShownSkip] = useState(false);
  const location = useLocation();

  const handleSkip = () => {
    const targetElement = document.getElementById(targetId);
    if (targetElement) {
      targetElement.focus();
      targetElement.scrollIntoView({ behavior: 'smooth' });

      // Simulate a Tab keypress to move to the first focusable element
      setTimeout(() => {
        const focusableElements = targetElement.querySelectorAll(
          'button, [tabindex]:not([tabindex="-1"]), input, select, textarea, a[href], [role="button"]',
        );

        // Filter out elements that are inside the student-toolbar
        const toolbarElement = document.querySelector('.teacher-top-app-bar');
        const filteredElements = Array.from(focusableElements).filter((element) => !toolbarElement?.contains(element));

        if (filteredElements.length > 0) {
          const firstFocusable = filteredElements[0];
          firstFocusable.focus();
        }

        const iframeElement = targetElement.querySelector('iframe');
        if (iframeElement instanceof HTMLIFrameElement) {
          iframeElement.focus();
          iframeElement.contentWindow?.postMessage({
            event: 'focus-first-tabbable',
            data: {
              targetId: 'main-content',
              triggerSelector: '[data-test="student-toolbar"]',
            },
          }, '*');
        }
      }, 100); // Small delay to ensure scroll completes

      setHasShownSkip(false); // Reset for next navigation session
      onSkip?.();
    }
  };

  const handleOnKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleSkip();
    }
  };

  useEffect(() => {
    const toolbarElement = document.querySelector(triggerSelector);
    if (!toolbarElement) return;

    let tabPressed = false;

    const handleKeyDown = (event) => {
      if (event.key === 'Tab') {
        tabPressed = true;
      }
    };

    const handleFocusIn = (event) => {
      const { target } = event;

      // Check if focus entered toolbar via Tab and skip hasn't been shown yet
      if (
        tabPressed
        && !hasShownSkip
        && toolbarElement.contains(target)
      ) {
        setHasShownSkip(true);
      }

      tabPressed = false; // Reset after processing
    };

    const handleFocusOut = (event) => {
      const { relatedTarget } = event;

      // Reset when focus leaves the toolbar completely
      if (!relatedTarget || (!toolbarElement.contains(relatedTarget) && relatedTarget)) {
        setHasShownSkip(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    // @ts-ignore
    toolbarElement.addEventListener('focusin', handleFocusIn);
    // @ts-ignore
    toolbarElement.addEventListener('focusout', handleFocusOut);

    // eslint-disable-next-line consistent-return
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      // @ts-ignore
      toolbarElement.removeEventListener('focusin', handleFocusIn);
      // @ts-ignore
      toolbarElement.removeEventListener('focusout', handleFocusOut);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerSelector, hasShownSkip, location.pathname]);

  // Reset skip state when route changes
  useEffect(() => {
    setHasShownSkip(false);
  }, [location.pathname]);

  return (
    <Button
      // ref={skipButtonRef}
      onClick={handleSkip}
      onKeyDown={handleOnKeyDown}
      tabIndex={0}
      sx={{
        position: 'absolute',
        top: -1000,
        left: 10,
        zIndex: 9999,
        backgroundColor: 'primary.main',
        color: 'white',
        padding: '8px 16px',
        fontSize: '14px',
        border: '2px solid transparent',
        '&:focus': {
          top: 10,
        },
        '&:hover': {
          backgroundColor: 'primary.dark',
        },
      }}
      data-test='skip-to-main-content'
    >
      Skip to main content
    </Button>
  );
}
